import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Account,
  Participant,
  Session,
  StudyType,
  useCurrentParticipant,
  useGetAccountParticipant,
  useMe,
} from '@app/api';
import { CenteredLoader } from '@app/core';

import { ParticipantInformations } from './ParticipantInformations';
import { isMisingParticipantInformation, LoginViaCodeContent } from '@app/auth';

interface Props {
  participantId?: string;
  onSubmit: (participant: Participant) => any;
  session?: Session;
  countryKey?: string;
}

enum Step {
  LOADING = 'LOADING',
  PARTICIPANT_INFO = 'PARTICIPANT_INFO',
  CODE_MAIL = 'CODE_MAIL',
}

export const SignupParticipant = ({
  participantId: invitationParticipantId,
  onSubmit,
  session,
  countryKey,
}: Props) => {
  const navigate = useNavigate();
  const sessionId = session?.id;

  const { data: me, isLoading, isFetched } = useMe(true);
  const getAccountParticipant = useGetAccountParticipant();
  const requirePhoneNumber = session?.studyType !== StudyType.UNMODERATED;

  // prefetching
  useCurrentParticipant(true);

  const isMeFirstLoading = isLoading && !isFetched;

  const [currentStep, setCurrentStep] = useState<Step>(Step.LOADING);

  const isReady = !isMeFirstLoading;
  const isInvitation = !!invitationParticipantId;

  const connectedWithRightAccount =
    invitationParticipantId &&
    me?.linkedParticipants?.includes(invitationParticipantId);

  const onLoginSuccess = useCallback(
    async (account: Account) => {
      const currentParticipant = await getAccountParticipant(account);

      if (!currentParticipant) {
        return setCurrentStep(Step.PARTICIPANT_INFO);
      }

      if (
        await isMisingParticipantInformation(
          currentParticipant,
          account,
          requirePhoneNumber,
        )
      ) {
        return setCurrentStep(Step.PARTICIPANT_INFO);
      }

      onSubmit(currentParticipant);
    },
    [onSubmit, getAccountParticipant, setCurrentStep, requirePhoneNumber],
  );

  const fallbackToRegistration = useCallback(() => {
    setCurrentStep(Step.PARTICIPANT_INFO);
  }, [setCurrentStep]);

  const initialCheck = useCallback(() => {
    const isLogged = !!me;
    if (isLogged) {
      if (isInvitation) {
        if (!connectedWithRightAccount) {
          return setCurrentStep(Step.CODE_MAIL);
        }
      }
      onLoginSuccess(me);
    } else {
      if (isInvitation) {
        return setCurrentStep(Step.CODE_MAIL);
      }

      setCurrentStep(Step.PARTICIPANT_INFO);
    }
  }, [
    connectedWithRightAccount,
    invitationParticipantId,
    me,
    isInvitation,
    setCurrentStep,
    onLoginSuccess,
  ]);

  const initialized = useRef(false);
  useEffect(() => {
    if (!sessionId || !isReady) {
      return;
    }
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    initialCheck();
  }, [isReady, sessionId, initialized, initialCheck]);

  if (!sessionId) {
    navigate('');
    return null;
  }

  if (currentStep === Step.LOADING) {
    return <CenteredLoader />;
  }

  if (currentStep === Step.CODE_MAIL) {
    return (
      <LoginViaCodeContent
        displayed
        participantId={invitationParticipantId}
        onSuccess={onLoginSuccess}
        onNoAccountForParticipant={fallbackToRegistration}
      />
    );
  }

  return (
    <ParticipantInformations
      onSuccess={onSubmit}
      session={session}
      participantId={invitationParticipantId}
      requirePhoneNumber={requirePhoneNumber}
      countryKey={countryKey}
    />
  );
};
